import {api} from "@/shared/api";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {ProjectTimeDetailsDialog} from "@/features/projecttime/projectTimeDetailsDialog";
import {WorkingTimeDetailsDialog} from "@/features/workingtime/workingTimeDetailsDialog";
import {WorkingTimeRequestDialog} from "@/features/requests/workingTimeRequestDialog";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {WorkingTimeDateSpanDialog} from "@/features/workingtime/workingTimeDateSpanDialog";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {TaskInfoDialog} from "@/features/task/taskInfoDialog";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {
	getWorkingTimeTypesForAddAbsence,
	getWorkingTimeTypesForAddWorkingTime,
	isProjectTimeDateTimeChangeAllowed
} from "@/shared/securityHelper";
import {
	AddProjectTimeEvent,
	AddWorkingTimeEvent,
	AddWorkingTimeNoticeEvent,
	DuplicateProjectTimeEvent,
	DuplicateWorkingTimeRequestEvent,
	EditWorkingTimeRequestEvent,
	ProjectTimeItem,
	ShowProjectTimeEvent,
	ShowTaskDetailsEvent,
	ShowWorkingTimeNoticeEvent,
	ShowWorkingTimeNoticeRequestEvent,
	ShowWorkingTimeEvent,
	ShowWorkingTimeRequestEvent,
	StatusUpdatedEvent,
	WorkingTimeItem,
	WorkingTimeTypeFilter,
	EditWorkingTimeEvent,
	EditProjectTimeEvent
} from "@/features/timeline/timelineTypes";
import {TimelineDaysView} from "@/features/timeline/views/timelineDaysView";
import {TimelineWeekView} from "@/features/timeline/views/timelineWeekView";
import {
	EVENT_PROJECT_TIME_ADD,
	EVENT_PROJECT_TIME_DELETED,
	EVENT_PROJECT_TIME_DUPLICATE,
	EVENT_PROJECT_TIME_EDIT,
	EVENT_PROJECT_TIME_SHOW,
	EVENT_PROJECT_TIME_SHOW_TASK_DETAILS,
	EVENT_PROJECT_TIME_STATUS_UPDATED,
	EVENT_WORKING_TIME_ADD,
	EVENT_WORKING_TIME_DELETED,
	EVENT_WORKING_TIME_DUPLICATE,
	EVENT_WORKING_TIME_EDIT,
	EVENT_WORKING_TIME_NOTICE_ADD,
	EVENT_WORKING_TIME_NOTICE_REQUEST_SHOW,
	EVENT_WORKING_TIME_NOTICE_SHOW,
	EVENT_WORKING_TIME_REQUEST_DUPLICATE,
	EVENT_WORKING_TIME_REQUEST_EDIT,
	EVENT_WORKING_TIME_REQUEST_SHOW,
	EVENT_WORKING_TIME_SHOW,
	EVENT_WORKING_TIME_STATUS_UPDATED
} from "@/features/timeline/timelineConstants";
import {getDialogPresetDateTime, isWorkingTimeDateSpanEditable} from "@/features/timeline/timelineHelper";
import {isProjectTimeIncluded, isWorkingTimeIncluded, RecordingType} from "@/features/timeline/recordingProcedure";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	formatDate,
	getStartOfConfiguredWeek,
	isSameDay,
	isSameOrBeforeDay,
	parseDateInput,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	AbstractWorkingTimeRequestResponseModel,
	CustomFieldDefinition,
	FindWorkingTimeDateSpanResponse,
	ReportProjectTime,
	ReportWorkingTime,
	TaskDetailsResponse,
	WorkingTimeNotice,
	WorkingTimeType
} from "@/shared/timr";
import {
	DateTimeChangeableByUserDayRangeMode,
	RecordingProcedure,
	TimelineMode,
	WorkingTimeRequestStatus
} from "@/shared/timrTypes";
import {mapProjectTimeToReportProjectTime, mapWorkingTimeToReportWorkingTime} from "@/shared/utils/recordMapper";
import {getWorkingTimeOrigin, WorkingTimeOrigin} from "@/shared/recording/workingTimeOrigin";
import {buildValidationsListItem} from "@/shared/validations/validationsListComponent";
import {Notify} from "@/shared/ui/notify";

export interface TimelineConfig {
	workingTimeTypes: WorkingTimeType[];
	customFieldDefinitionsWorkingTime: CustomFieldDefinition[];
	workingTimeTypesForNotices: WorkingTimeType[];
	defaultStatusNumberWorkingTime: number;
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	defaultStatusNumberProjectTime: number;
	isGroupLeaderForThemself: boolean;
	workingTimeDateTimeChangeableByUserDayRangeUntil: string | null;
	workingTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	projectTimeDateTimeChangeableByUserDayRangeUntil: string | null;
	projectTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	workingTimeStatusNumbers: number[];
	projectTimeStatusNumbers: number[];
	activeRecordingProcedure: RecordingProcedure;
	recordingFunctionsActive: boolean;
	reportingOtherUsers: boolean;
	projectTimeAttachmentsEnabled: boolean;
}

/**
 * Base class for page classes that have a timeline integrated.
 * Takes care of the date navigation, dialogs (including details dialog), and the timelineDays component that builds the timeline.
 * The personnel and report timeline are derived from this base class
 */
export abstract class BaseTimelinePage {
	// constants for date shift functions
	private static DAYS_COUNT_DAY: number = 1;
	private static DAYS_COUNT_WEEK: number = 7;

	protected dateInput: HTMLInputElement;
	protected dateFromInput: HTMLInputElement;
	protected dateToInput: HTMLInputElement;
	protected todayButton: HTMLButtonElement;
	protected sortButton: HTMLButtonElement;
	protected projectTimeDialog: ProjectTimeDialog;
	protected workingTimeDialog: WorkingTimeDialog;
	protected projectTimeDetailsDialog: ProjectTimeDetailsDialog;
	protected workingTimeDetailsDialog: WorkingTimeDetailsDialog;
	protected workingTimeRequestDialog: WorkingTimeRequestDialog;
	protected workingTimeNoticeDialog: WorkingTimeNoticeDialogBridge;
	protected workingTimeDateSpanDialog: WorkingTimeDateSpanDialog;
	protected timelineComponent: TimelineDaysView | TimelineWeekView;
	protected modeControl: ButtonRadioGroup<TimelineMode>;
	protected taskInfoDialog: TaskInfoDialog;

	protected constructor(public config: TimelineConfig, initialDate: Temporal.PlainDate, initialMode: TimelineMode, initialSortDescending: boolean) {
		this.initDetailsDialog();
		this.initWorkingTimeNoticeDialog();
		this.initListeners(initialMode);
		this.drawComponent(initialMode, initialSortDescending);
		this.initDateFieldValues(initialDate);
	}

	/**
	 * Initializes the task information dialog with default settings.
	 * DEVNOTE: The task links beta feature and 'start project time with this task' is disabled by default
	 * 			Because this needs to be specified how it behaves in combined recording, see TIMR-9886 (comments)
	 * @return {void} This method does not return a value.
	 */
	protected abstract initTaskInfoDialog(): void;

	/**
	 * Displays the task information dialog for the specified task ID and user ID.
	 * This is opened via context menu of a project time item.
	 *
	 * @param taskId
	 * @param userId
	 * @protected
	 */
	protected showTaskInfoDialog(taskId: number, userId: number): void {
		// inform user that no task dialog is available if project time is disabled
		if (!globals.projectTimeEnabled) {
			BootstrapModal.info({message: messages["recording.projectTime.taskInfo.featureNotActivated"]});
			return;
		}

		if (!this.taskInfoDialog) {
			this.initTaskInfoDialog();
		}
		void api("tasks/loadTaskDetails.json")
			.get({id: taskId})
			.done((response: TaskDetailsResponse) => {
				this.taskInfoDialog.show(response, userId);
			});
	}

	/**
	 * Update sessionSettings variables and reloads timeline
	 * @protected
	 */
	protected abstract onInputChanged(): void;

	protected abstract onAddWorkingTime(workingTimeTypeFilter: WorkingTimeTypeFilter, start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void;

	/**
	 * Opens dialogs for add working time/absence button click (top right corner)
	 * The dialog uses has a filtered list of working time types, preselects user and date of the report filter inputs
	 * Time fields are prefilled by now for the start and now + 30 min for the end
	 * @param workingTimeTypeFilter method to filter working time types (working time/absence)
	 * @private
	 */
	protected onAddWorkingTimeButtonClicked(workingTimeTypeFilter: WorkingTimeTypeFilter): void {
		const {start, end} = this.getAddButtonPresetDateTime("workingTime");

		// Open dialog with given working time types and start/end time for date input prefilling
		this.onAddWorkingTime(workingTimeTypeFilter, start, end);
	}

	/**
	 * Opens dialogs for the "Add Project Time" button click event.
	 * This method retrieves the preset preselects user and date of the report filter inputs
	 * Time fields are prefilled by now for the start and now + 30 min for the end
	 */
	protected onAddProjectTimeButtonClicked(): void {
		const {start, end} = this.getAddButtonPresetDateTime("projectTime");
		this.onAddProjectTime(start, end);
	}

	/**
	 * Determines the start/end date time preset for the top bar "add" button.
	 * By default the currently selected timeline date (single day in daily mode, week start in weekly mode) is combined with
	 * the current time. Subclasses may override this to apply a different logic (e.g. last-used entry end in weekly mode).
	 */
	protected getAddButtonPresetDateTime(type: RecordingType): { start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime } {
		return getDialogPresetDateTime(this.getTimelineDate());
	}

	protected initDialogListeners(): void {
		$(this.workingTimeDialog)
			.on(RecordDialog.EVENT_RECORD_CREATED, () => this.reload()) // there is already a notification for creating a recording
			.on(RecordDialog.EVENT_REQUEST_CREATED, () => this.reload())
			.on(RecordDialog.EVENT_RECORD_EDITED, () => this.reload(messages["recording.saved"]))
			.on(RecordDialog.EVENT_RECORD_DELETED, () => this.reload(messages["recording.deleted"]));
		$(this.projectTimeDialog)
			.on(RecordDialog.EVENT_RECORD_CREATED, () => this.reload()) // there is already a notification for creating a recording
			.on(RecordDialog.EVENT_RECORD_EDITED, () => this.reload(messages["recording.saved"]))
			.on(RecordDialog.EVENT_RECORD_DELETED, () => this.reload(messages["recording.deleted"]));
	}

	protected onAddProjectTime(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		this.projectTimeDialog.create();
		if (start && end) {
			this.projectTimeDialog.setStartEndTime(start, end);
		}
	}

	/**
	 * Retrieves a PlainDate based on the date inputs and the selected mode.
	 * If the mode is daily, it uses the dateInput value (single date field visible).
	 * Otherwise, it uses the dateFromInput value (from and to date fields visible).
	 *
	 * @return PlainDate representing the date parsed from the input values.
	 */
	protected getTimelineDate(): Temporal.PlainDate | null {
		return parseDateInput(this.isDailyModeSelected() ? this.dateInput.value : this.dateFromInput.value);
	}

	/**
	 * Initializes date listeners for the timeline navigation.
	 *
	 * This function sets up event listeners for date inputs and navigation buttons to handle date changes and shifts
	 */
	private initDateListeners(): void {
		this.dateInput = document.getElementById("timelineNavigationDailyDate") as HTMLInputElement;
		this.dateFromInput = document.getElementById("timelineNavigationWeeklyDateFrom") as HTMLInputElement;
		this.dateToInput = document.getElementById("timelineNavigationWeeklyDateTo") as HTMLInputElement;
		this.todayButton = document.getElementById("timelineNavigationTodayButton") as HTMLButtonElement;
		this.sortButton = document.getElementById("timelineNavigationSortToggle") as HTMLButtonElement;

		const cacheValue = (e: FocusEvent) => {
			const target = $(e.target);
			target.data("data-current-val", target.val());
		}
		const updateDateOnChange = (e: JQuery.TriggeredEvent) => {
			const target = $(e.target);
			if (target.data("data-current-val") !== target.val()) {
				const parsed = parseDateInput(target.val() as string);
				if (parsed) {
					this.updateDateFields(parsed);
				}
			}
		}

		// cache value to avoid reloads on focus in and out without a change
		this.dateInput.addEventListener("focusin", cacheValue);
		this.dateFromInput.addEventListener("focusin", cacheValue);
		this.dateToInput.addEventListener("focusin", cacheValue);

		// check if date has changed and update date
		// DEVNOTE "apply.daterangepicker" is required if the month is shifted inside the picker before date selection. This only works with jQuery event handler .on()
		$(this.dateInput).on("apply.daterangepicker focusout", updateDateOnChange);
		$(this.dateFromInput).on("apply.daterangepicker focusout", updateDateOnChange);
		$(this.dateToInput).on("apply.daterangepicker focusout", updateDateOnChange);

		// add event listeners for date navigation buttons
		document.getElementById("timelineNavigationDailyPrevious").addEventListener("click", () => this.shiftDate(-BaseTimelinePage.DAYS_COUNT_DAY));
		document.getElementById("timelineNavigationDailyNext").addEventListener("click", () => this.shiftDate(BaseTimelinePage.DAYS_COUNT_DAY));
		document.getElementById("timelineNavigationWeeklyPrevious").addEventListener("click", () => this.shiftDate(-BaseTimelinePage.DAYS_COUNT_WEEK));
		document.getElementById("timelineNavigationWeeklyNext").addEventListener("click", () => this.shiftDate(BaseTimelinePage.DAYS_COUNT_WEEK));

		// add event listener for today button
		document.getElementById("timelineNavigationTodayButton").addEventListener("click", () => this.updateDateFields(Temporal.Now.plainDateISO(), true));
	}

	/**
	 * Initialize listeners for date filter input including navigation buttons, add working/vacation button click (top right corner)
	 *
	 * @param initialMode The initial TimelineMode to set up listeners for.
	 * @private
	 */
	private initListeners(initialMode: TimelineMode): void {
		this.initDateListeners();

		/* add event listeners for add recording buttons of top right corner */
		if (isWorkingTimeIncluded(this.config.activeRecordingProcedure)) {
			const addWorkingTimeButton = document.querySelector("#addWorkingTimeButton");
			const addAbsenceButton = document.querySelector("#addAbsenceButton");

			addWorkingTimeButton.addEventListener("click", () => this.onAddWorkingTimeButtonClicked(getWorkingTimeTypesForAddWorkingTime));
			addWorkingTimeButton.classList.toggle("disabled", getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes, this.config.reportingOtherUsers).length === 0);

			addAbsenceButton.addEventListener("click", () => this.onAddWorkingTimeButtonClicked(getWorkingTimeTypesForAddAbsence));
			addAbsenceButton.classList.toggle("disabled", getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes, this.config.reportingOtherUsers).length === 0);
		}

		if (isProjectTimeIncluded(this.config.activeRecordingProcedure)) {
			const addProjectTimeButton = document.querySelector("#addProjectTimeButton");
			addProjectTimeButton.addEventListener("click", () => this.onAddProjectTimeButtonClicked());
			addProjectTimeButton.classList.toggle("disabled", !isProjectTimeDateTimeChangeAllowed(this.config.reportingOtherUsers));
		}

		// preselect mode, daily mode as fallback if no session variable saved
		const preselectWeeklyMode = initialMode !== TimelineMode.Daily;
		this.modeControl = ButtonRadioGroup.typed({
			value: TimelineMode.Daily,
			button: document.getElementById("timelineModeDailyButton"),
			selected: !preselectWeeklyMode
		}, {
			value: TimelineMode.Weekly,
			button: document.getElementById("timelineModeWeeklyButton"),
			selected: preselectWeeklyMode
		}).onValueChanged((mode: TimelineMode) => {
			// update visibility of daily or weekly date fields
			this.toggleDateFields(mode);

			// update date fields based on the single date field
			this.updateDateFields(parseDateInput(this.dateInput.value) ?? Temporal.Now.plainDateISO());

			// draw daily or weekly timeline component
			this.drawComponent(mode, this.isSortDescending());

			// update session setting variables and reload timeline
			this.onInputChanged();
		});

		// timeline sort order toggle button
		this.sortButton.addEventListener("click", () => {
			this.applySortState(!this.isSortDescending()); // toggle current sort state
			this.onInputChanged();
		});
	}

	/**
	 * Toggles the visibility of date fields based on the specified TimelineMode
	 * Date fields visible for both modes:
	 * - DAILY -> single date
	 * - WEEKLY -> from and to date
	 * @param mode The TimelineMode to determine which date fields to display.
	 */
	private toggleDateFields(mode: TimelineMode): void {
		(document.getElementById("timelineNavigationGroupDaily") as HTMLInputElement).style.display = mode === TimelineMode.Daily ? "table" : "none";
		(document.getElementById("timelineNavigationGroupWeekly") as HTMLInputElement).style.display = mode === TimelineMode.Weekly ? "table" : "none";
	}

	/**
	 * Updates the state of the "Today" button.
	 * Also updates the current day number of the calendar icon
	 * <p>
	 * The button is disabled if the selected date is today (in daily mode) or if today's date is within the selected date range (in weekly mode).
	 */
	protected updateTodayButtonState(): void {
		const today = Temporal.Now.plainDateISO();

		// disable the today button selected date is today (daily mode) or if mode is weekly and today's date is within the selected date range
		this.todayButton.disabled = this.modeControl.getValue() === TimelineMode.Daily
			? isSameDay(this.getTimelineDate(), today)
			: isSameOrBeforeDay(parseDateInput(this.dateFromInput.value), today) && isSameOrBeforeDay(today, parseDateInput(this.dateToInput.value));

		// update this number of the today's button calendar icon with the current day inside
		this.todayButton.querySelector("[data-calendar-number]").textContent = today.day.toString();
	}

	/**
	 * Applies the specified sort order to the timeline and updates the UI accordingly.
	 * Also updates the timeline toggle button's data attribute to reflect the current sort state, which is used for persistence
	 * @param descending A boolean value indicating whether the timeline items should be sorted in descending order (true) or ascending order (false).
	 * @private
	 */
	private applySortState(descending: boolean): void {
		// update data attribute for sort state
		this.sortButton.dataset["timelineSortDescending"] = descending.toString();

		// update sort icon of toggle button
		const icon = this.sortButton.querySelector("i");
		icon.classList.toggle("fa-sort-amount-down", !descending);
		icon.classList.toggle("fa-sort-amount-up", descending);

		// update timeline styling (to apply different spacing of items and visibility for time dots)
		if (this.timelineComponent) {
			this.timelineComponent.classList.toggle("timeline-items-ascending", !descending);
			this.timelineComponent.classList.toggle("timeline-items-descending", descending);
			this.timelineComponent.config = {
				...this.timelineComponent.config,
				timelineSortDescending: descending
			};
		}
	}

	/**
	 * Returns whether the timeline items are currently sorted in descending order.
	 */
	protected isSortDescending(): boolean {
		return this.sortButton.dataset["timelineSortDescending"] === "true";
	}

	/**
	 * Checks if the selected mode is DAILY (selected value of buttonRadioGroup is DAILY)
	 * @return true if the mode control value is set to DAILY, false otherwise (WEEKLY)
	 */
	protected isDailyModeSelected(): boolean {
		return this.modeControl.getValue() === TimelineMode.Daily;
	}

	/**
	 * Selects all date fields based on a single provided date
	 *
	 * This method calculates the start and end of the week considering the first day of the week setting.
	 * It then updates the date inputs with the formatted start and end dates of the week.
	 *
	 * @param date The date string used to determine the week range.
	 * @param updateTimeline If true, timeline is reloaded with the selected values after updating the date fields (true by default)
	 */
	protected updateDateFields(date: Temporal.PlainDate, updateTimeline: boolean = true): void {
		// get start and end of week considering first day of week setting
		const startOfWeek = getStartOfConfiguredWeek(date);
		const endOfWeek = startOfWeek.add({days: BaseTimelinePage.DAYS_COUNT_WEEK - 1});

		this.dateInput.value = formatDate(date);
		this.dateFromInput.value = formatDate(startOfWeek);
		this.dateToInput.value = formatDate(endOfWeek);

		if (updateTimeline) {
			this.onInputChanged();
		}
	}

	/**
	 * Shifts the date range by adding the specified number of days to both the start and end dates.
	 * Based on the mode daily/weekly, a single or both from and to date fields are used.
	 *
	 * @param addDays The number of days to add to the current date range.
	 */
	private shiftDate(addDays: number): void {
		const date = parseDateInput(this.dateInput.value);
		const dateFrom = parseDateInput(this.dateFromInput.value);
		const dateTo = parseDateInput(this.dateToInput.value);

		this.dateFromInput.value = formatDate(dateFrom?.add({days: addDays}));
		this.dateToInput.value = formatDate(dateTo?.add({days: addDays}));
		this.dateInput.value = formatDate(date?.add({days: addDays}));

		this.onInputChanged();
	}

	/**
	 * Reloads component<p>
	 * If message is transmitted a notification will be displayed with this message
	 * @param notificationMessage
	 * @private
	 */
	protected reload(notificationMessage?: string): void {
		this.timelineComponent.reload();
		if (notificationMessage) {
			Notify.success(notificationMessage);
		}
	}

	/**
	 * Create timeline day component
	 * <p>
	 * Therefore a web component is generated (based on the selected mode), event listener are applied
	 * And the component appended to the timelineContent
	 */
	private drawComponent(mode: TimelineMode, descending: boolean): void {
		// create web component
		this.timelineComponent = mode === TimelineMode.Daily ?
			document.createElement("timeline-days") as TimelineDaysView :
			document.createElement("timeline-week") as TimelineWeekView;

		// set config
		this.timelineComponent.config = {
			customFieldDefinitionsWorkingTime: this.config.customFieldDefinitionsWorkingTime,
			customFieldDefinitionsProjectTime: this.config.customFieldDefinitionsProjectTime,
			workingTimeTypes: this.config.workingTimeTypes,
			activeRecordingProcedure: this.config.activeRecordingProcedure,
			recordingFunctionsActive: this.config.recordingFunctionsActive,
			reportingOtherUsers: this.config.reportingOtherUsers,
			workingTimeTypesForNotices: this.config.workingTimeTypesForNotices,
			workingTimeStatusNumbers: this.config.workingTimeStatusNumbers,
			projectTimeStatusNumbers: this.config.projectTimeStatusNumbers,
			mode: mode,
			projectTimeAttachmentsEnabled: this.config.projectTimeAttachmentsEnabled,
			timelineSortDescending: descending
		};
		this.applySortState(descending);

		// init event listeners to trigger opening dialogs
		this.initEventListeners();

		const timelineContent = document.getElementById("timelineContent");
		// empty content container
		timelineContent.innerHTML = "";

		// append timeline component
		timelineContent.append(this.timelineComponent);
	}

	/**
	 * Initializes the event listener for the component<p>
	 * E.g. Recording dialogs are opened here by listening to events triggered by clicking in the individual components
	 */
	protected initEventListeners(): void {
		/* working time */
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_ADD, e => {
			const customEvent = e as CustomEvent<AddWorkingTimeEvent>;
			this.onAddWorkingTime(customEvent.detail.workingTimeTypeFilter, customEvent.detail.start, customEvent.detail.end);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_SHOW, e => {
			const customEvent = e as CustomEvent<ShowWorkingTimeEvent>;
			this.onShowWorkingTimeItem(customEvent.detail.workingTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_EDIT, e => {
			const customEvent = e as CustomEvent<EditWorkingTimeEvent>;
			this.onEditWorkingTimeItem(customEvent.detail.workingTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_DUPLICATE, e => {
			const customEvent = e as CustomEvent<ShowWorkingTimeEvent>;
			this.onDuplicateWorkingTimeItem(customEvent.detail.workingTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_REQUEST_SHOW, e => {
			const customEvent = e as CustomEvent<ShowWorkingTimeRequestEvent>;
			this.onShowWorkingTimeRequest(customEvent.detail.workingTimeRequest);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_REQUEST_EDIT, e => {
			const customEvent = e as CustomEvent<EditWorkingTimeRequestEvent>;
			this.onEditWorkingTimeRequest(customEvent.detail.workingTimeRequest);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_REQUEST_DUPLICATE, e => {
			const customEvent = e as CustomEvent<DuplicateWorkingTimeRequestEvent>;
			this.onDuplicateWorkingTimeRequest(customEvent.detail.workingTimeRequest);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_STATUS_UPDATED, e => {
			const customEvent = e as CustomEvent<StatusUpdatedEvent>;
			this.reload(messages.withArguments("timeline.status.updated", customEvent.detail.statusName));
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_NOTICE_SHOW, e => {
			const customEvent = e as CustomEvent<ShowWorkingTimeNoticeEvent>;
			this.onShowWorkingTimeNotice(customEvent.detail.workingTimeNotice)
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_NOTICE_REQUEST_SHOW, e => {
			const customEvent = e as CustomEvent<ShowWorkingTimeNoticeRequestEvent>;
			this.onShowWorkingTimeNoticeRequest(customEvent.detail.workingTimeRequestId);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_NOTICE_ADD, e => {
			const customEvent = e as CustomEvent<AddWorkingTimeNoticeEvent>;
			this.onAddWorkingTimeNotice(customEvent.detail.start, customEvent.detail.end);
		});
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_DELETED, () => this.reload(messages["recording.deleted"]));

		/* project time */
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_ADD, e => {
			const customEvent = e as CustomEvent<AddProjectTimeEvent>;
			this.onAddProjectTime(customEvent.detail.start, customEvent.detail.end);
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_SHOW, e => {
			const customEvent = e as CustomEvent<ShowProjectTimeEvent>;
			this.onShowProjectTimeItem(customEvent.detail.projectTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_EDIT, e => {
			const customEvent = e as CustomEvent<EditProjectTimeEvent>;
			this.onEditProjectTimeItem(customEvent.detail.projectTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_DUPLICATE, e => {
			const customEvent = e as CustomEvent<DuplicateProjectTimeEvent>;
			this.onDuplicateProjectTimeItem(customEvent.detail.projectTimeItem);
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_SHOW_TASK_DETAILS, e => {
			const customEvent = e as CustomEvent<ShowTaskDetailsEvent>;
			this.showTaskInfoDialog(customEvent.detail.projectTime.task.id, customEvent.detail.projectTime.user.id);
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_STATUS_UPDATED, e => {
			const customEvent = e as CustomEvent<StatusUpdatedEvent>;
			this.reload(messages.withArguments("timeline.status.updated", customEvent.detail.statusName));
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_DELETED, () => this.reload(messages["recording.deleted"]));
	}

	/**
	 * Initializes details dialog which is opened by a click on a working or project time item<br>
	 * Inside it's possible to open the edit dialog
	 * @private
	 */
	private initDetailsDialog(): void {
		this.workingTimeDetailsDialog = new WorkingTimeDetailsDialog({
			title: messages["workingTime.details.header"],
			dialogId: "#workingTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			editRecord: (workingTime: ReportWorkingTime) => this.onEditWorkingTime(workingTime)
		});
		this.projectTimeDetailsDialog = new ProjectTimeDetailsDialog({
			title: messages["projectTime.details.header"],
			dialogId: "#projectTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			editRecord: (projectTime: ReportProjectTime) => this.onEditProjectTime(projectTime)
		});
	}

	/**
	 * Initializes the date field values by setting them with session variables.
	 * This method ensures that the timeline is not reloaded again by passing `false` for `updateTimeline`.
	 * Additionally, it adjusts the visibility of date fields based on the current mode and updates the state of the "Today" button.
	 *
	 * @param initialDate The initial date to set in the date fields.
	 * @return {void} Does not return a value.
	 */
	private initDateFieldValues(initialDate: Temporal.PlainDate): void {
		// sets the date fields with the initial values, the timeline is already reloaded in the constructors of the derived classes, to avoid double loading, "false" is passed for updateTimeline
		this.updateDateFields(initialDate, false);
		this.toggleDateFields(this.modeControl.getValue());
		this.updateTodayButtonState();
	}

	/**
	 * Opens the working time details dialog for the given working time item (see {@link openWorkingTimeItem}).
	 *
	 * @param workingTimeItem - The working time item to be displayed in the dialog.
	 */
	private onShowWorkingTimeItem(workingTimeItem: WorkingTimeItem): void {
		this.openWorkingTimeItem(workingTimeItem, workingTime => this.workingTimeDetailsDialog.show(workingTime));
	}

	/**
	 * Opens the working time edit dialog for the given working time item (see {@link openWorkingTimeItem}).
	 *
	 * @param workingTimeItem - The working time item to be edited in the dialog.
	 */
	private onEditWorkingTimeItem(workingTimeItem: WorkingTimeItem): void {
		this.openWorkingTimeItem(workingTimeItem, workingTime => this.onEditWorkingTime(workingTime));
	}

	/**
	 * Opens the dialog matching the origin of the given working time item (see {@link getWorkingTimeOrigin}):
	 * the request dialog, the DateSpan dialog or the given standard working time dialog,
	 * which is also the fallback if the request or DateSpan can't be loaded.
	 *
	 * @param workingTimeItem - The working time item to be opened.
	 * @param openWorkingTimeDialog - Opens the standard working time (details) dialog.
	 */
	private openWorkingTimeItem(workingTimeItem: WorkingTimeItem, openWorkingTimeDialog: (workingTime: ReportWorkingTime) => void): void {
		const reportWorkingTime = this.mapWorkingTimeForDialog(workingTimeItem);
		switch (getWorkingTimeOrigin(reportWorkingTime)) {
			case WorkingTimeOrigin.Request:
				api("requests/findWorkingTimeRequestByWorkingTimeId.json")
					.get({workingTimeId: reportWorkingTime.id})
					.done((workingTimeRequest) => this.showOrEditWorkingTimeRequest(workingTimeRequest))
					.fail(() => openWorkingTimeDialog(reportWorkingTime));
				break;
			case WorkingTimeOrigin.DateSpan:
				api("workingTimeDateSpans/findWorkingTimeDateSpanWithValidationsByWorkingTimeId.json")
					.get({workingTimeId: reportWorkingTime.id})
					.done((response) => this.showOrEditWorkingTimeDateSpan(response, reportWorkingTime))
					.fail(() => openWorkingTimeDialog(reportWorkingTime));
				break;
			case WorkingTimeOrigin.Standalone:
				openWorkingTimeDialog(reportWorkingTime);
				break;
		}
	}

	/**
	 * Opens the project time details dialog for the given project time item.
	 * This method prepares the project time data - creates a copy and entry updates properties for proper working dialog and displays the dialog.
	 *
	 * @param projectTimeItem - The project time item to be displayed in the dialog.
	 */
	private onShowProjectTimeItem(projectTimeItem: ProjectTimeItem): void {
		this.projectTimeDetailsDialog.show(this.mapProjectTimeForDialog(projectTimeItem));
	}

	/**
	 * Opens the project time edit dialog for the given project time item.
	 *
	 * @param projectTimeItem - The project time item to be edited in the dialog.
	 */
	private onEditProjectTimeItem(projectTimeItem: ProjectTimeItem): void {
		this.onEditProjectTime(this.mapProjectTimeForDialog(projectTimeItem));
	}

	private onEditWorkingTime(workingTime: ReportWorkingTime): void {
		this.workingTimeDialog.edit(workingTime);
	}

	private onEditProjectTime(projectTime: ReportProjectTime): void {
		this.projectTimeDialog.edit(projectTime);
	}

	private onDuplicateWorkingTimeItem(workingTimeItem: WorkingTimeItem): void {
		const reportWorkingTime = this.mapWorkingTimeForDialog(workingTimeItem);
		switch (getWorkingTimeOrigin(reportWorkingTime)) {
			case WorkingTimeOrigin.Request:
				api("requests/findWorkingTimeRequestByWorkingTimeId.json")
					.get({workingTimeId: reportWorkingTime.id})
					.done((workingTimeRequest) => this.getWorkingTimeRequestDialog().duplicate(workingTimeRequest, this.config.reportingOtherUsers))
					.fail(() => this.workingTimeDialog.duplicate(reportWorkingTime));
				break;
			case WorkingTimeOrigin.DateSpan:
				api("workingTimeDateSpans/findWorkingTimeDateSpanByWorkingTimeId.json")
					.get({workingTimeId: reportWorkingTime.id})
					.done((workingTimeDateSpan) => this.getWorkingTimeDateSpanDialog().duplicate(workingTimeDateSpan, this.config.reportingOtherUsers))
					.fail(() => this.workingTimeDialog.duplicate(reportWorkingTime));
				break;
			case WorkingTimeOrigin.Standalone:
				this.workingTimeDialog.duplicate(reportWorkingTime);
				break;
		}
	}

	private onDuplicateProjectTimeItem(projectTimeItem: ProjectTimeItem): void {
		this.projectTimeDialog.duplicate(this.mapProjectTimeForDialog(projectTimeItem));
	}

	private onShowWorkingTimeRequest(workingTimeRequest: ShowWorkingTimeRequestEvent["workingTimeRequest"]): void {
		this.withLoadedWorkingTimeRequest(
			workingTimeRequest.id,
			request => this.getWorkingTimeRequestDialog().show(request)
		);
	}

	private onEditWorkingTimeRequest(workingTimeRequest: EditWorkingTimeRequestEvent["workingTimeRequest"]): void {
		this.withLoadedWorkingTimeRequest(
			workingTimeRequest.id,
			request => this.getWorkingTimeRequestDialog().edit(request)
		);
	}

	private onDuplicateWorkingTimeRequest(workingTimeRequest: DuplicateWorkingTimeRequestEvent["workingTimeRequest"]): void {
		this.withLoadedWorkingTimeRequest(
			workingTimeRequest.id,
			request => this.getWorkingTimeRequestDialog().duplicate(request, this.config.reportingOtherUsers)
		);
	}

	private withLoadedWorkingTimeRequest(
		workingTimeRequestId: number,
		action: (workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => void
	): void {
		api("requests/findWorkingTimeRequestById.json")
			.get({workingTimeRequestId})
			.done(action);
	}

	private onShowWorkingTimeNotice(workingTimeNotice: WorkingTimeNotice): void {
		this.workingTimeNoticeDialog.openForEdit(workingTimeNotice);
	}

	/**
	 * Opens the request dialog for a pending notice request or for the request a notice was created from.
	 */
	private onShowWorkingTimeNoticeRequest(workingTimeRequestId: number): void {
		this.withLoadedWorkingTimeRequest(
			workingTimeRequestId,
			wtr => this.showOrEditWorkingTimeRequest(wtr)
		);
	}

	/**
	 * Opens the working time notice dialog to create a new notice with the selected day prefilled.
	 * The base (personal timeline) creates the notice for the current user, so no user is preselected and the
	 * dialog hides the user field. The reporting timeline overrides this to preselect the shown user.
	 *
	 * @param start start date preselected in the dialog
	 * @param end end date preselected in the dialog
	 */
	protected onAddWorkingTimeNotice(start: Temporal.PlainDate | null, end: Temporal.PlainDate | null): void {
		this.workingTimeNoticeDialog.openForCreate({start, end});
	}

	private showOrEditWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): void {
		if (workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested) {
			this.getWorkingTimeRequestDialog().edit(workingTimeRequest);
			return;
		}

		this.getWorkingTimeRequestDialog().show(workingTimeRequest);
	}

	private showOrEditWorkingTimeDateSpan(response: FindWorkingTimeDateSpanResponse, selectedWorkingTime: ReportWorkingTime): void {
		const validationsListItem = buildValidationsListItem(selectedWorkingTime.id, response.validations, true);

		if (isWorkingTimeDateSpanEditable(selectedWorkingTime)) {
			this.getWorkingTimeDateSpanDialog().edit(response.workingTimeDateSpan, validationsListItem);
			return;
		}

		this.getWorkingTimeDateSpanDialog().show(response.workingTimeDateSpan, validationsListItem);
	}

	protected getWorkingTimeRequestDialog(): WorkingTimeRequestDialog {
		if (!this.workingTimeRequestDialog) {
			this.workingTimeRequestDialog = new WorkingTimeRequestDialog({
				dialogId: "#workingTimeRequestDialog",
				workingTimeTypes: this.config.workingTimeTypes,
				isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
				userSelectionEnabled: this.config.reportingOtherUsers,
				customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime
			});
			$(this.workingTimeRequestDialog)
				.on(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED, () => this.reload(messages["recording.saved"]))
				.on(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED, () => this.reload())
				.on(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED, () => this.reload(messages["recording.deleted"]));
		}
		return this.workingTimeRequestDialog;
	}

	private initWorkingTimeNoticeDialog(): void {
		this.workingTimeNoticeDialog = new WorkingTimeNoticeDialogBridge({
			workingTimeTypes: this.config.workingTimeTypesForNotices,
			showUserRow: this.config.reportingOtherUsers,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself
		});
		$(this.workingTimeNoticeDialog)
			.on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_SAVED, () => this.reload(messages["recording.saved"]))
			.on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_DELETED, () => this.reload(messages["recording.deleted"]));
	}

	protected getWorkingTimeDateSpanDialog(): WorkingTimeDateSpanDialog {
		if (!this.workingTimeDateSpanDialog) {
			this.workingTimeDateSpanDialog = new WorkingTimeDateSpanDialog({
				dialogId: "#workingTimeDateSpanDialog",
				workingTimeTypes: this.config.workingTimeTypes,
				userSelectionEnabled: this.config.reportingOtherUsers
			});
			$(this.workingTimeDateSpanDialog)
				.on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_EDITED, () => this.reload(messages["recording.saved"]))
				.on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_DELETED, () => this.reload(messages["recording.deleted"]));
		}
		return this.workingTimeDateSpanDialog;
	}

	/**
	 * Maps a working time of WorkingTimeItem object to a WorkingTime object for dialog usage
	 * This is necessary due to differences in types of WorkingTime/WorkingTimeDTO (dialog uses individual record map instead WorkingTimeDTO)
	 * Note that for certain action still some adaptions are necessary
	 *
	 * @param {WorkingTimeItem} workingTimeItem - The input working time item containing data to be mapped.
	 * @return {WorkingTime} A copy of the WorkingTime object with mapped and transformed properties for dialog representation.
	 */
	private mapWorkingTimeForDialog(workingTimeItem: WorkingTimeItem): ReportWorkingTime {
		return mapWorkingTimeToReportWorkingTime(workingTimeItem.workingTime, workingTimeItem.validationResults);
	}

	/**
	 * Maps a project time of ProjectTimeItem object to a ProjectTime object for dialog usage
	 * This is necessary due to differences in types of ProjectTime/ProjectTimeDTO (dialog uses individual record map instead ProjectTimeDTO)
	 * Note that for certain action still some adaptions are necessary
	 *
	 * @param {ProjectTimeItem} projectTimeItem - The input project time item containing data to be mapped.
	 * @return {ReportProjectTime} A copy of the ProjectTime object with mapped and transformed properties for dialog representation.
	 */
	private mapProjectTimeForDialog(projectTimeItem: ProjectTimeItem): ReportProjectTime {
		return mapProjectTimeToReportProjectTime(projectTimeItem.projectTime, projectTimeItem.validationResults);
	}
}
